import type { Measure, NoteEvent } from '../../music/piece';
import { barAt } from '../../music/piece';
import { midiOf, parsePitch } from '../../music/pitch';
import type { ChoraleFile, ChoraleJson } from './types';

/**
 * The compact chorale format written by scripts/import_chorales.py, and its decoder.
 *
 * Each sung line is one string of tokens in time order:
 *   NAME/DUR   a note ("F#4/0.5"), a trailing ~ marks a fermata
 *   r/DUR      a rest
 *   &NAME/DUR  a note starting together with the previous one
 *   @T         move the cursor to time T (a line that overlaps itself)
 * Times are in quarter notes from the start of the first (pickup) measure.
 */

const round = (x: number) => Math.round(x * 1e6) / 1e6;

export function decodeMeasures(file: Pick<ChoraleFile, 'measures'>): Measure[] {
  return file.measures.map(([bar, start, length]) => ({ bar, start, length }));
}

export function decodeVoice(text: string, line: number, measures: Measure[], lyrics?: string[]): NoteEvent[] {
  const out: NoteEvent[] = [];
  let cursor = 0;
  let prevOnset = 0;
  let noteIndex = 0;
  for (const tok of text.split(' ')) {
    if (!tok) continue;
    if (tok[0] === '@') {
      cursor = Number(tok.slice(1));
      continue;
    }
    const chord = tok[0] === '&';
    let body = chord ? tok.slice(1) : tok;
    const fermata = body.endsWith('~');
    if (fermata) body = body.slice(0, -1);
    const slash = body.indexOf('/');
    const name = body.slice(0, slash);
    const dur = Number(body.slice(slash + 1));
    const onset = round(chord ? prevOnset : cursor);
    if (name !== 'r') {
      const note: NoteEvent = { bar: barAt(measures, onset), onset, dur, midi: midiOf(parsePitch(name)), name, line };
      if (fermata) note.fermata = true;
      const syl = lyrics?.[noteIndex];
      if (syl) note.lyric = syl;
      out.push(note);
      noteIndex++;
    }
    prevOnset = onset;
    cursor = round(chord ? Math.max(cursor, onset + dur) : onset + dur);
  }
  return out;
}

export function decodeNotes(file: Pick<ChoraleFile, 'measures' | 'voices' | 'lyrics'>): NoteEvent[] {
  const measures = decodeMeasures(file);
  return file.voices
    .flatMap((v, line) => decodeVoice(v, line, measures, line === 3 ? file.lyrics : undefined))
    .sort((a, b) => a.onset - b.onset || a.midi - b.midi);
}

export function decodeHarmony(file: Pick<ChoraleFile, 'harmony'>): ChoraleJson['harmony'] {
  return file.harmony.map(([bar, beat, key, rn, pivot]) => {
    const h: ChoraleJson['harmony'][number] = { bar, beat, key, rn };
    if (pivot) {
      const i = pivot.indexOf(':');
      h.pivot = { key: pivot.slice(0, i).trim(), rn: pivot.slice(i + 1).trim() };
    }
    return h;
  });
}

export function decodeChorale(file: ChoraleFile): ChoraleJson {
  const { measures: _m, voices: _v, lyrics: _l, harmony: _h, ...meta } = file;
  return { ...meta, measures: decodeMeasures(file), notes: decodeNotes(file), harmony: decodeHarmony(file) };
}

/** Harmony back to the compact tuples (used by scripts/align_chorales.ts). */
export function encodeHarmony(harmony: ChoraleJson['harmony']): ChoraleFile['harmony'] {
  return harmony.map((h) => {
    const t: ChoraleFile['harmony'][number] = [h.bar, h.beat, h.key, h.rn];
    if (h.pivot) t.push(`${h.pivot.key}: ${h.pivot.rn}`);
    return t;
  });
}
